import {Component, effect, ElementRef, input, output, signal, viewChild} from '@angular/core';
import {ReservationService} from '../../shared/services/reservation.service';
import {finalize} from 'rxjs/operators';
import {AlertService, DateUtils, isNotNullOrUndefined} from 'sigale-angular';
import {DateUtilsService} from '../../shared/services/date-utils.service';
import {Reservation} from '../../shared/components/calendrier/model/reservation.model';
import {Ilot} from '../../reserver-un-bureau/model/ilot.model';
import {Bureau} from '../../reserver-un-bureau/model/bureau.model';
import {IlotDto} from '../../shared/dto/ilot.dto';
import {TranslateService} from '@ngx-translate/core';

@Component({
	selector: 'app-detail-reservation-modal',
	templateUrl: './detail-reservation-modal.component.html',
	styleUrl: './detail-reservation-modal.component.scss'
})
export class DetailReservationModalComponent {
	readonly NOM_ALERTE_DETAIl_RESERVATION_MODAL = 'alertDetailReservationModal';

	containerAffichageIlot = viewChild<ElementRef>('containerAffichageIlot');

	visible = input.required<boolean>();
	reservation = input.required<Reservation>();
	langueUtilisateur = input.required<string>();

	visibleChange = output<boolean>();
	notificationReservationAnnule = output<Reservation>();

	longueurParent = signal<number>(50);
	hauteurParent = signal<number>(50);

	loadingConstructionIlot = signal<boolean>(false);
	loadingAnnulation = signal<boolean>(false);
	visibleLocal = signal(false);
	ilot = signal<Ilot>(null);

	labelHaut = signal('');
	labelDroite = signal('');
	labelBas = signal('');
	labelGauche = signal('');

	constructor(
		private reservationService: ReservationService,
		private alertService: AlertService,
		private dateUtilsService: DateUtilsService,
		private translateService: TranslateService
	) {
		// A chaque modification de visible qui est renseigné par le parent (ex à l'ouverture de la modale) on notifie modifie le visible local
		effect(() => {
			this.visibleLocal.set(this.visible());
		}, {allowSignalWrites: true});

		// Chaque fois que le visible en local est modifié on emit la valeur au parent.
		effect(() => {
			const val = this.visibleLocal();
			this.visibleChange.emit(val);
		});
		effect(() => {
			this.recupererIlotDeLaReservation();
		}, {allowSignalWrites: true});
	}

	/**
	 * Formatte la date pour l'affichage.
	 * @param date la date à formatter.
	 */
	formattageDateSansHeurePourAffichage(date: Date) {
		return this.dateUtilsService.formaterDateEnFormatLettre(new Date(date), this.langueUtilisateur());
	}

	/**
	 * Annule la réservation.
	 */
	annulerReservation(): void {
		this.loadingAnnulation.set(true);
		this.reservationService.annulerUneReservation(this.reservation().id)
			.pipe(finalize(() => this.loadingAnnulation.set(false)))
			.subscribe({
				next: () => {
					this.notificationReservationAnnule.emit(this.reservation());
					this.fermerModal();
				},
				error: (error) => {
					this.alertService.clear(this.NOM_ALERTE_DETAIl_RESERVATION_MODAL);
					this.alertService.error(error.error, this.NOM_ALERTE_DETAIl_RESERVATION_MODAL);
				}
			});
	}

	/**
	 * Effectue les actions de fermeture de modal
	 */
	fermerModal(): void {
		this.visibleChange.emit(false);
		this.visibleLocal.set(false);
		this.alertService.clear(this.NOM_ALERTE_DETAIl_RESERVATION_MODAL);
	}

	/**
	 * Vrai si la date de réservation est supérieure ou égale à aujourd'hui, faux sinon.
	 */
	estAfficherBoutonAnnulation(): boolean {
		return !DateUtils.isDayBefore(this.reservation().date, new Date());
	}

	recupererIlotDeLaReservation() {
		this.loadingConstructionIlot.set(true);

		const dateReservation = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(new Date(this.reservation().date), this.langueUtilisateur());

		this.reservationService.recupererIlotPourIlotId(this.reservation().ilotId, dateReservation)
			.pipe(finalize(() => this.loadingConstructionIlot.set(false)))
			.subscribe({
				next: (zoneDeTravailAvecIlotDto) => {
					const bureaux: Bureau[] = [];
					const ilot: IlotDto = zoneDeTravailAvecIlotDto.ilot;

					this.labelHaut.set(zoneDeTravailAvecIlotDto.indicateurHaut ?? '');
					this.labelDroite.set(zoneDeTravailAvecIlotDto.indicateurDroite ?? '');
					this.labelBas.set(zoneDeTravailAvecIlotDto.indicateurBas ?? '');
					this.labelGauche.set(zoneDeTravailAvecIlotDto.indicateurGauche ?? '');

					ilot.bureaux.forEach(bureauDto => {
						const bureau = new Bureau(this.translateService, bureauDto, bureauDto.coordonneeX, bureauDto.coordonneeY);

						bureaux.push(bureau);
					});

					this.ilot.set(new Ilot(ilot, bureaux));

					const longueurContainer = this.containerAffichageIlot().nativeElement.offsetWidth;
					const hauteurContainer = this.containerAffichageIlot().nativeElement.offsetHeight;

					this.longueurParent.set(longueurContainer);
					this.hauteurParent.set(hauteurContainer);
				},
				error: (error) => {
					this.alertService.clear(this.NOM_ALERTE_DETAIl_RESERVATION_MODAL);
					this.alertService.error(error.error, this.NOM_ALERTE_DETAIl_RESERVATION_MODAL);
				}
			});
	}

	protected readonly isNotNullOrUndefined = isNotNullOrUndefined;
}
